// Room scenarios for "Exposure in the Dark: The Phantom of the Darkroom"
// Each room defines: target exposure, motion / DOF constraints, scene visuals.

export const APERTURE_OPTIONS = [
    { label: "f/1.4", value: "1.4", stops: 1, dof: "very_shallow" },
    { label: "f/2.8", value: "2.8", stops: 3, dof: "shallow" },
    { label: "f/5.6", value: "5.6", stops: 5, dof: "medium" },
    { label: "f/8",   value: "8",   stops: 6, dof: "deep" },
    { label: "f/16",  value: "16",  stops: 8, dof: "very_deep" },
];

export const SHUTTER_OPTIONS = [
    { label: "1 s",     value: "1",     stops: 0  },
    { label: "1/15",    value: "1/15",  stops: 4  },
    { label: "1/60",    value: "1/60",  stops: 6  },
    { label: "1/250",   value: "1/250", stops: 8  },
    { label: "1/1000",  value: "1/1000",stops: 10 },
];

export const ISO_OPTIONS = [
    { label: "100",  value: "100",  stops: 0 },
    { label: "400",  value: "400",  stops: 2 },
    { label: "1600", value: "1600", stops: 4 },
    { label: "6400", value: "6400", stops: 6 },
];

// targetEV = aperture.stops + shutter.stops - iso.stops (the EV the player must "match")
// Smaller targetEV = darker scene.
export const ROOMS = [
    {
        id: "antechamber",
        index: 0,
        name: "Safelight Antechamber",
        flavor:
            "A red bulb hums overhead. You hear chemicals trays clink. Photograph the developer tray to find the key beneath it.",
        targetEV: 5,        // moderate dim
        evTolerance: 1,
        motion: "still",
        dofRequirement: null,
        sceneClass: "scene-safelight",
        teaches: "Balance the triangle. Start here.",
        practiceHint: "Dim scene, no motion. Aim for EV ~5: open the aperture, slow the shutter, and lift ISO a little — but watch grain.",
        winLesson: "Nice triangle. You balanced light across all three dials — no single one had to work too hard.",
    },
    {
        id: "running_shadow",
        index: 1,
        name: "The Running Shadow",
        flavor:
            "Something fast is sprinting across the hallway. The Phantom's apprentice? Capture it sharp — not as a smear.",
        targetEV: 7,
        evTolerance: 1,
        motion: "fast",          // requires shutter.stops >= 8
        motionRequirement: 8,
        dofRequirement: null,
        sceneClass: "scene-corridor",
        teaches: "Shutter speed freezes motion.",
        practiceHint: "Subject is FAST. Use 1/250 or faster, then balance the loss of light with ISO.",
        winLesson: "Your fast shutter froze the runner mid-stride. That's exactly how sports and street photographers catch action clean.",
    },
    {
        id: "locked_cabinet",
        index: 2,
        name: "The Locked Cabinet",
        flavor:
            "A brass key dangles inches from the lens, mounted on the cabinet glass. Isolate it from the cluttered room behind.",
        targetEV: 8,
        evTolerance: 1,
        motion: "still",
        dofRequirement: { type: "shallow", maxApertureStops: 3 },  // aperture <= f/2.8
        sceneClass: "scene-cabinet",
        teaches: "Aperture controls depth of field.",
        practiceHint: "You need a SHALLOW depth of field. Use f/2.8 or wider to isolate the key from the cluttered background.",
        winLesson: "A wide aperture blurred everything behind the key. That's shallow depth of field — the storyteller's trick for pointing the eye.",
    },
    {
        id: "emergency_strobe",
        index: 3,
        name: "Emergency Strobe",
        flavor:
            "Red alarms pulse. The hallway flashes bone-white, ten times a second. Do not over-expose, or the Phantom sees you.",
        targetEV: 11,
        evTolerance: 1,
        motion: "still",
        dofRequirement: null,
        sceneClass: "scene-strobe",
        teaches: "Bright scenes need fast shutter or small aperture.",
        practiceHint: "Scene is BRIGHT. Close the aperture or use a faster shutter — and keep ISO low (100) to avoid over-exposure.",
        winLesson: "You stopped down the aperture (or hit a fast shutter) so the strobe didn't blow out. Bright scenes need fewer stops of light — not more.",
    },
    {
        id: "phantom",
        index: 4,
        name: "The Phantom of the Darkroom",
        flavor:
            "He stands at the end of the corridor, swaying. One clean shot will banish him. Be quick. Be quiet. Be clean.",
        targetEV: 5,
        evTolerance: 1,
        motion: "fast",
        motionRequirement: 8,
        noiseCeilingStops: 4,    // ISO must be <= 1600. Higher noise wakes him.
        sceneClass: "scene-boss",
        teaches: "Master all three: light, speed, and silence.",
        practiceHint: "Boss room: fast shutter to freeze him, wide aperture for light, ISO at most 1600 (higher = he hears you).",
        winLesson: "Fast shutter, wide aperture, ISO under the ceiling. You handled all three sides of the triangle at once — the mark of a real photographer.",
        boss: true,
    },
];

export const TOTAL_ROOMS = ROOMS.length;
